import type {
  PickerSource,
  ProviderVariant,
} from 'slices/aiConversations/pickerTypes'

/** One `provider_variants` element, as thanx-dbt builds it */
export interface ProviderVariantWire {
  provider: string
  provider_uid: string
  provider_global_uid?: string | null
  provider_name?: string | null
  location_id?: number | null
  price_cents?: number | null
  uid_group?: string | null
  resolved_global_uid?: string | null
}

export const toProviderVariant = (
  wire: ProviderVariantWire
): ProviderVariant => ({
  provider: wire.provider,
  providerUid: wire.provider_uid,
  providerGlobalUid: wire.provider_global_uid ?? null,
  providerName: wire.provider_name ?? null,
  locationId: wire.location_id ?? null,
  priceCents: wire.price_cents ?? null,
  uidGroup: wire.uid_group ?? null,
  resolvedGlobalUid: wire.resolved_global_uid ?? null,
})

/** A `snowflake/query` record: every value arrives as text, arrays as JSON text */
export type SnowflakeRecordWire = Record<string, string | number | null>

export interface SnowflakeRowsWire {
  records: SnowflakeRecordWire[]
  pagination: { total_count: number }
}

/** The lowest and highest price a version sells at, in cents */
export interface PriceRange {
  minCents: number
  maxCents: number
}

/** A canonical product, read from a level's smart source */
export interface ProductRow {
  kind: 'product'
  id: string
  name: string
  /** null until loaded: lists don't fetch them */
  variants: ProviderVariant[] | null
}

/** One provider's group of uids, read from a level's specific source */
export interface UidGroupRow {
  kind: 'uidGroup'
  /** Unique across providers, which can share a uid_group */
  id: string
  provider: string
  uidGroup: string
  name: string
  /** The group is a provider's global UID, which also covers store UIDs mapped to it later */
  isGlobal: boolean
  /** The canonical product the group was picked under */
  canonicalId: string
  canonicalName: string
  /** The locations that carry it, including ones the merchant can't see */
  locationIds: number[]
  /** null on a group read before the lookups asked for them */
  storeUids: string[] | null
  /** null until loaded, as its store uids are, or when it has no price */
  priceRange: PriceRange | null
  /**
   * Every canonical product the group's variants belong to, which scope level
   * 2; a group can span more than one. null until looked up by key.
   */
  productIds: string[] | null
}

/** A typed or pasted uid the merchant added though nothing holds it, so it targets nothing */
export interface UnmatchedUidRow {
  kind: 'unmatchedUid'
  id: string
  uid: string
  /** The uid itself: nothing else names it */
  name: string
}

/** One store UID of a UID group, picked alone: it targets only itself, not the rest of the group */
export interface StoreUidRow {
  kind: 'storeUid'
  id: string
  provider: string
  uid: string
  /** Its canonical product's name */
  name: string
  /** What the store calls it, or its group's name when that isn't known */
  storeName: string
  canonicalId: string
  /** Every canonical product it reaches, which scope level 2 */
  productIds: string[]
  /** The locations that sell it; null when it was picked without them */
  locationIds: number[] | null
}

export type PickerRow = ProductRow | UidGroupRow | StoreUidRow | UnmatchedUidRow

/** The kinds of row a source lists */
export type PickerRowKind = Exclude<
  PickerRow['kind'],
  'storeUid' | 'unmatchedUid'
>

export interface PickerRowsPage {
  rows: PickerRow[]
  totalCount: number
}

export interface PickerRowsArgs {
  source: PickerSource
  kind: PickerRowKind
  search: string
  page: number
  /** Canonical ids a scoped source is filtered to; null for an unscoped one */
  scope: string[] | null
}

export interface PickerRowsByKeyArgs {
  source: PickerSource
  kind: PickerRowKind
  keys: string[]
  /** Canonical ids a scoped source is filtered to; null for an unscoped one */
  scope: string[] | null
}

/** The UID groups behind smart picks, read from the specific source */
export interface UidGroupsOfProductsArgs {
  source: PickerSource
  /** Where the products' variants are read; null when the level keeps none */
  variantsSource: PickerSource | null
  productIds: string[]
  /** Canonical ids a scoped variants source is filtered to; null for an unscoped one */
  scope: string[] | null
}

/** A fixed list's UID groups under level 1's picks */
export interface ScopedUidGroupsArgs {
  source: PickerSource
  /** The smart source of the same level, which names its products under level 1's picks */
  scopeSource: PickerSource
  /** Level 1's canonical ids */
  scope: string[]
}

/** The store UIDs of one provider's UID group, read from the source's details source */
export interface StoreUidsArgs {
  source: PickerSource
  group: UidGroupRow
}

export interface PickerRowsByUidArgs {
  source: PickerSource
  uids: string[]
}

/** The groups that hold the uids looked up, as their group or a store UID, and the uids none holds */
export interface UidsLookup {
  groups: UidGroupRow[]
  unmatchedUids: string[]
}
